import { Add, KeyboardArrowDown, Remove } from '@mui/icons-material';
import { Box, Button, Collapse, Typography } from '@mui/material';
import { AppContext } from 'context/AppContextProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { AnyOfPermissions, IRouteProps } from 'routes/types';
import { isRoutePermitted } from 'routes/routeUtils';
import { Permissions } from 'services/enums';
import { getLicenseId, hasAllPermission } from 'services/DefaultService';
import { fontColorDarkTheme } from 'styles/global.styles';

const ACCENT_COLOR = 'var(--app-accent)';
const ACCENT_BG = 'var(--app-accent-bg)';

interface SidebarMenuItemProps {
  route: IRouteProps;
  pathname: string;
  isOpenedSidebar: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  isChild?: boolean;
  onNavigate?: () => void;
}

export const checkIfContainPermission = (route: IRouteProps) => {
  return (
    (route.key !== 'management' && !hasAllPermission()) ||
    hasAllPermission()
  );
};

export const isRouteRenderable = (
  route: IRouteProps,
  isRouteAllowed: (permission?: Permissions | Permissions[] | AnyOfPermissions) => boolean,
): boolean => {
  if (route.key === 'management' || !checkIfContainPermission(route)) return false;
  const hasChildren = route.collapse && route.collapse.length > 0;
  if (hasChildren) {
    return route.collapse!.some((c) => isRoutePermitted(c, isRouteAllowed, getLicenseId()));
  }
  return isRoutePermitted(route, isRouteAllowed, getLicenseId());
};

const PILL_LEFT_PADDING = 2.25;

export const SidebarMenuItem = ({
  route,
  pathname,
  isOpenedSidebar,
  isExpanded,
  onToggle,
  isChild = false,
  onNavigate,
}: SidebarMenuItemProps) => {
  const { t } = useTranslation();
  const { isRouteAllowed } = useContext(AppContext);
  const hasChildren = route.collapse && route.collapse.length > 0;
  const permittedChildren = hasChildren ? route.collapse!.filter((c) => isRoutePermitted(c, isRouteAllowed, getLicenseId())) : [];

  if (route.key === 'management' || !checkIfContainPermission(route)) {
    return null;
  }
  
  if (hasChildren ? permittedChildren.length === 0 : !isRoutePermitted(route, isRouteAllowed, getLicenseId())) {
    return null;
  }

  const isRouteActive = route.route === pathname;
  const isChildActive = permittedChildren.some((c) => c.route === pathname);
  const isHighlighted = isRouteActive || isChildActive;

  const childrenToShow =
    hasChildren && isOpenedSidebar
      ? isExpanded
        ? permittedChildren
        : permittedChildren.filter((c) => c.route === pathname)
      : [];

  const renderEndIcon = () => {
    if (!hasChildren || !isOpenedSidebar) return null;
    const iconSx = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 24,
      height: 24,
      borderRadius: '50%',
      transition: 'background-color 0.15s',
    };

    if (isChildActive) {
      return (
        <Box className="collapse-icon-circle" title={isExpanded ? 'Mostrar menos' : 'Mostrar mais'} sx={iconSx}>
          <Box
            key={isExpanded ? 'minus' : 'plus'}
            sx={{
              display: 'flex',
              animation: 'sidebarCollapseIconPop 0.18s ease',
              '@keyframes sidebarCollapseIconPop': {
                from: { opacity: 0, transform: 'scale(0.5) rotate(-45deg)' },
                to: { opacity: 1, transform: 'scale(1) rotate(0deg)' },
              },
            }}
          >
            {isExpanded ? <Remove sx={{ fontSize: 18 }} /> : <Add sx={{ fontSize: 18 }} />}
          </Box>
        </Box>
      );
    }
    return (
      <Box className="collapse-icon-circle" title={isExpanded ? 'Mostrar menos' : 'Mostrar mais'} sx={iconSx}>
        <KeyboardArrowDown
          sx={{
            fontSize: 18,
            transition: 'transform 0.2s ease',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ ml: isChild ? 2.5 : 0 }}>
      <NavLink
        to={route.route ?? pathname}
        style={{ textDecoration: 'none' }}
        onClick={(e) => {
          if (route.isDisabled) return e.preventDefault();
          if (hasChildren) {
            e.preventDefault();
            onToggle();
          } else {
            onNavigate?.();
          }
        }}
      >
        <Button
          disabled={route.isDisabled}
          variant="text"
          endIcon={renderEndIcon()}
          startIcon={route.icon}
          sx={{
            color: route.isDisabled ? '#a1a1a1' : isHighlighted ? ACCENT_COLOR : fontColorDarkTheme,
            backgroundColor: isHighlighted ? ACCENT_BG : 'transparent',
            borderRadius: 2,
            width: '100%',
            minWidth: 0,
            height: 44,
            p: 1.5,
            pl: isOpenedSidebar ? PILL_LEFT_PADDING : 0,
            pr: isOpenedSidebar ? 1.75 : 0,
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: isOpenedSidebar ? 'flex-start' : 'center',
            textTransform: 'none',
            transition: 'background-color 0.15s, border-color 0.15s',
            '&.Mui-disabled': { color: '#595959FF' },
            '&:hover': {
              backgroundColor: isHighlighted ? ACCENT_BG : 'rgba(255,255,255,0.06)',
            },
            '&:hover .collapse-icon-circle': {
              backgroundColor: 'rgba(255,255,255,0.15)',
            },
            '& .MuiButton-startIcon': {
              marginRight: isOpenedSidebar ? undefined : 0,
              marginLeft: isOpenedSidebar ? undefined : 0,
            },
          }}
        >
          <Typography
            sx={{
              mr: 'auto',
              ml: 0,
              fontSize: 14,
              display: isOpenedSidebar ? 'block' : 'none',
              visibility: isOpenedSidebar ? 'visible' : 'hidden',
              opacity: isOpenedSidebar ? 1 : 0,
              whiteSpace: 'nowrap',
              transition: 'visibility 0s, opacity 0.5s linear',
            }}
          >
            {t(route.name ?? 'default.route.name')}
          </Typography>
        </Button>
      </NavLink>

      <Collapse
        in={childrenToShow.length > 0}
        timeout={{ enter: 250, exit: 350 }}
        easing={{ enter: 'ease-out', exit: 'ease-in-out' }}
        unmountOnExit
      >
        {childrenToShow.map((childRoute) => (
          <SidebarMenuItem
            key={childRoute.key}
            route={childRoute}
            pathname={pathname}
            isOpenedSidebar={isOpenedSidebar}
            isExpanded={false}
            onToggle={() => {}}
            isChild={true}
            onNavigate={() => {
              if (isExpanded) onToggle();
              onNavigate?.();
            }}
          />
        ))}
      </Collapse>
    </Box>
  );
};
